import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Landmark, IndianRupee, TrendingUp, TrendingDown, Building2, Plus, ClipboardCheck, Check, X, LogOut, Receipt, RefreshCw } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};
const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

const TABS = [
  { id: 'dashboard', label: 'Group Dashboard' },
  { id: 'expenses',  label: 'HO Expenses' },
  { id: 'approvals', label: 'HO Approvals' },
];

const HO_CATEGORY_LABELS = {
  ho_rent: 'HO Office Rent',
  ho_staff_salary: 'HO Staff Salaries',
  ho_electricity: 'HO Electricity',
  ho_internet: 'HO Internet',
  ho_software: 'HO Software Fees',
  ho_other: 'HO Other',
};

const HOPortal = () => {
  const nav = useNavigate();
  const { user, logout, hasPermission } = useApp();
  const canEditExpenses = hasPermission('ho_expenses', 'edit');
  const canApprove = hasPermission('ho_approvals', 'edit');
  const [tab, setTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [approvals, setApprovals] = useState(null);
  const [expForm, setExpForm] = useState({ category: 'ho_rent', amount: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const isAuthorised = user && (user.role === 'chief_accountant' || user.role === 'admin');

  const load = async () => {
    if (!isAuthorised) return;
    setLoading(true);
    try {
      const [d, e, a] = await Promise.all([
        axios.get(`${API}/ho/dashboard`, authCfg()),
        axios.get(`${API}/ho/expenses`, authCfg()),
        axios.get(`${API}/ho/approvals?status=pending`, authCfg()),
      ]);
      setDash(d.data); setExpenses(e.data); setApprovals(a.data);
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to load' }); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  if (!user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>;
  if (!isAuthorised) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="ho-forbidden">
        <div className="bg-white rounded-2xl p-8 border border-red-200 max-w-md">
          <p className="font-bold text-lg text-red-700">Access denied</p>
          <p className="text-sm text-neutral-600 mt-2">Only Chief Accountant or Super Admin can view the HO portal.</p>
          <button onClick={() => nav('/')} className="mt-4 px-4 py-2 rounded-lg bg-neutral-900 text-white text-sm font-semibold">Home</button>
        </div>
      </div>
    );
  }

  const createExpense = async () => {
    const amt = parseInt(expForm.amount, 10);
    if (!amt || amt <= 0) { setMsg({ ok: false, text: 'Amount must be > 0' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/ho/expenses`, { category: expForm.category, amount: amt, note: expForm.note }, authCfg());
      setMsg({ ok: true, text: `Recorded ${HO_CATEGORY_LABELS[expForm.category]}: ${fmt(amt)}` });
      setExpForm({ category: 'ho_rent', amount: '', note: '' });
      load();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed' }); }
    finally { setBusy(false); }
  };

  const decide = async (id, kind) => {
    const reason = window.prompt(`${kind === 'approve' ? 'Approve' : 'Reject'} entry — optional reason:`, '');
    if (reason === null) return;
    try {
      await axios.post(`${API}/ho/approvals/${id}/${kind}`, { reason }, authCfg());
      setMsg({ ok: true, text: `${kind}d entry ${id.slice(0, 10)}` });
      load();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed' }); }
  };

  const totalPending = (approvals?.counts?.franchise || 0) + (approvals?.counts?.ho || 0);

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="ho-portal">
      {/* Top bar */}
      <div className="bg-[#4338CA] text-white px-5 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
          <Landmark className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-extrabold text-sm">Head Office · Corporate Accounting</p>
          <p className="text-[11px] text-indigo-100">{user.name} · {user.role === 'admin' ? 'Super Admin (override)' : 'Chief Accountant'} · {user.email}</p>
        </div>
        <button data-testid="ho-refresh" onClick={load} className="p-2 rounded-lg hover:bg-white/10"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
        <button data-testid="ho-logout" onClick={() => { logout(); nav('/'); }} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20">
          <LogOut className="w-3.5 h-3.5" /> Logout
        </button>
      </div>

      <div className="max-w-6xl mx-auto p-5">
        {/* tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`ho-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${tab === t.id ? 'bg-[#4338CA] text-white' : 'bg-white border border-neutral-200 text-neutral-600 hover:border-[#4338CA]'}`}>
              {t.label}{t.id === 'approvals' && totalPending > 0 && <span className="ml-1.5 inline-block bg-red-500 text-white rounded-full px-1.5 text-[10px]">{totalPending}</span>}
            </button>
          ))}
        </div>

        {msg && (
          <div data-testid="ho-msg" className={`mb-3 px-3 py-2 rounded-lg text-xs ${msg.ok ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            {msg.text}
          </div>
        )}

        {loading && <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>}

        {/* Group Dashboard */}
        {!loading && tab === 'dashboard' && dash && (
          <div data-testid="ho-dashboard">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Kpi label="Global Commission" value={fmt(dash.global_commission)} color="#2E7D32" bg="#E8F5E9" icon={TrendingUp} testid="ho-kpi-commission" />
              <Kpi label="HO Expenses" value={fmt(dash.ho_expenses_total)} color="#C2410C" bg="#FEF3C7" icon={TrendingDown} testid="ho-kpi-expenses" />
              <Kpi label="Company Net Profit" sub="Commission − HO overheads" value={fmt(dash.company_net_profit)} color={dash.company_net_profit >= 0 ? '#065F46' : '#991B1B'} bg={dash.company_net_profit >= 0 ? '#D1FAE5' : '#FEE2E2'} icon={dash.company_net_profit >= 0 ? TrendingUp : TrendingDown} testid="ho-kpi-cnp" />
              <Kpi label="HO Cash + Bank" value={fmt(dash.ho_cash_and_bank_total)} color="#1E88E5" bg="#DBEAFE" icon={Landmark} testid="ho-kpi-cash" />
            </div>

            <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden mb-4">
              <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50 flex items-center gap-2">
                <p className="font-bold text-sm flex-1">Per-district performance ({dash.per_franchise.length} franchises)</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                    <tr>
                      <Th>District</Th><Th className="text-right">Revenue</Th><Th className="text-right">Expenses</Th><Th className="text-right">Cleaner Paid</Th><Th className="text-right">Net</Th><Th className="text-right">GST</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {dash.per_franchise.map((f) => (
                      <tr key={f.franchise_id} data-testid={`ho-franchise-row-${f.franchise_id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="px-4 py-2.5">
                          <p className="text-xs font-semibold">{f.district}</p>
                          <p className="text-[10px] text-neutral-400 font-mono">{f.franchise_id}</p>
                        </td>
                        <td className="px-4 py-2.5 text-right text-xs font-bold text-[#2E7D32]">{fmt(f.revenue)}</td>
                        <td className="px-4 py-2.5 text-right text-xs text-red-600">{fmt(f.expenses)}</td>
                        <td className="px-4 py-2.5 text-right text-xs text-[#0891B2]">{fmt(f.cleaner_paid)}</td>
                        <td className={`px-4 py-2.5 text-right text-xs font-bold ${f.net >= 0 ? 'text-[#065F46]' : 'text-red-600'}`}>{fmt(f.net)}</td>
                        <td className="px-4 py-2.5 text-right text-xs text-[#D97706]">{fmt(f.gst_total)}</td>
                      </tr>
                    ))}
                    {dash.per_franchise.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-xs text-neutral-400">No franchises yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {Object.keys(dash.ho_expenses_by_category || {}).length > 0 && (
              <div className="bg-white border border-neutral-200 rounded-2xl p-4">
                <p className="font-bold text-sm mb-3">HO expense breakdown</p>
                <div className="grid md:grid-cols-2 gap-2">
                  {Object.entries(dash.ho_expenses_by_category).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between text-xs px-3 py-2 rounded-lg bg-neutral-50">
                      <span className="font-semibold text-neutral-700">{HO_CATEGORY_LABELS[k] || k}</span>
                      <span className="font-bold text-red-600">{fmt(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* HO Expenses */}
        {!loading && tab === 'expenses' && expenses && (
          <div data-testid="ho-expenses-panel">
            <div className="bg-white border border-neutral-200 rounded-2xl p-4 mb-4">
              <p className="font-bold text-sm mb-3">Record HO expense</p>
              <div className="grid md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-neutral-600">Category</label>
                  <select data-testid="ho-exp-category" value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })}
                    className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
                    {expenses.categories.map((c) => <option key={c} value={c}>{HO_CATEGORY_LABELS[c] || c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-neutral-600">Amount (₹)</label>
                  <input data-testid="ho-exp-amount" type="number" value={expForm.amount}
                    onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })}
                    className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="45000" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-neutral-600">Note</label>
                  <input data-testid="ho-exp-note" value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })}
                    className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="Feb 2026 office rent" />
                </div>
              </div>
              <button data-testid="ho-exp-submit" onClick={createExpense} disabled={busy || !canEditExpenses}
                title={canEditExpenses ? '' : 'You lack Edit permission for HO Expenses'}
                className="mt-4 inline-flex items-center gap-1.5 py-2 px-4 rounded-lg bg-[#4338CA] hover:bg-[#3730A3] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm">
                <Plus className="w-4 h-4" /> {busy ? 'Recording…' : 'Record HO expense'}
              </button>
            </div>

            <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-neutral-100 flex items-center gap-2">
                <p className="font-bold text-sm flex-1">Total recorded: {fmt(expenses.total)}</p>
                <span className="text-[11px] text-neutral-500">{expenses.entries.length} entries</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                    <tr><Th>Date</Th><Th>Category</Th><Th>Note</Th><Th>Status</Th><Th className="text-right">Amount</Th></tr>
                  </thead>
                  <tbody>
                    {expenses.entries.map((e) => (
                      <tr key={e.id} data-testid={`ho-exp-row-${e.id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                        <td className="px-4 py-2 text-[11px] text-neutral-600">{(e.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                        <td className="px-4 py-2 text-xs font-semibold">{HO_CATEGORY_LABELS[e.category] || e.category}</td>
                        <td className="px-4 py-2 text-xs text-neutral-500 max-w-xs truncate">{e.note || '—'}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${e.approval_status === 'approved' ? 'bg-green-100 text-green-700' : e.approval_status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-800'}`}>{e.approval_status}</span>
                        </td>
                        <td className="px-4 py-2 text-right text-xs font-bold text-red-600">{fmt(e.amount)}</td>
                      </tr>
                    ))}
                    {expenses.entries.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-xs text-neutral-400">No HO expenses recorded yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* HO Approvals (dual queue) */}
        {!loading && tab === 'approvals' && approvals && (
          <div data-testid="ho-approvals-panel" className="space-y-4">
            <ApprovalGroup title="HO-owned pending" testid="ho-approvals-ho" rows={approvals.ho_pending || []} decide={decide} tone="indigo" canDecide={canApprove} />
            <ApprovalGroup title="Franchise pending (all districts)" testid="ho-approvals-franchise" rows={approvals.franchise_pending || []} decide={decide} tone="green" canDecide={canApprove} />
          </div>
        )}
      </div>
    </div>
  );
};

const Th = ({ children, className = '' }) => (
  <th className={`px-4 py-2 text-[10px] font-bold text-neutral-500 uppercase ${className}`}>{children}</th>
);

const Kpi = ({ label, value, sub, color, bg, icon: Icon, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-xl p-4">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: bg }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="mt-3 text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">{label}</p>
    <p className="font-extrabold text-lg mt-0.5" style={{ color }}>{value}</p>
    {sub && <p className="text-[10px] text-neutral-400 mt-0.5">{sub}</p>}
  </div>
);

const ApprovalGroup = ({ title, rows, decide, tone, testid, canDecide = true }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
    <div className={`px-4 py-3 border-b border-neutral-100 ${tone === 'indigo' ? 'bg-indigo-50 text-indigo-800' : 'bg-green-50 text-green-800'} flex items-center gap-2`}>
      <ClipboardCheck className="w-4 h-4" />
      <p className="font-bold text-sm flex-1">{title}</p>
      <span className="text-xs">{rows.length}</span>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
          <tr><Th>Date</Th><Th>Account</Th><Th>Category</Th><Th>Franchise</Th><Th>Note</Th><Th className="text-right">Amount</Th><Th className="text-right">Actions</Th></tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`ho-approval-row-${r.id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
              <td className="px-4 py-2 text-[11px] text-neutral-600">{(r.created_at || '').slice(0, 16).replace('T', ' ')}</td>
              <td className="px-4 py-2 text-xs font-semibold">{r.account}</td>
              <td className="px-4 py-2 text-[11px] text-neutral-500">{r.category || '—'}</td>
              <td className="px-4 py-2 text-[11px] text-neutral-600">{r.district || r.franchise_id || '—'}</td>
              <td className="px-4 py-2 text-[11px] text-neutral-500 max-w-xs truncate">{r.note || '—'}</td>
              <td className="px-4 py-2 text-right text-xs font-bold text-red-600">{fmt(r.amount)}</td>
              <td className="px-4 py-2 text-right space-x-1 whitespace-nowrap">
                {canDecide ? (
                  <>
                    <button data-testid={`ho-approve-${r.id}`} onClick={() => decide(r.id, 'approve')} className="inline-flex items-center gap-1 bg-green-100 hover:bg-green-200 text-green-700 text-[11px] font-semibold px-2 py-1 rounded-md">
                      <Check className="w-3 h-3" /> Approve
                    </button>
                    <button data-testid={`ho-reject-${r.id}`} onClick={() => decide(r.id, 'reject')} className="inline-flex items-center gap-1 bg-red-100 hover:bg-red-200 text-red-700 text-[11px] font-semibold px-2 py-1 rounded-md">
                      <X className="w-3 h-3" /> Reject
                    </button>
                  </>
                ) : (
                  <span data-testid={`ho-decide-locked-${r.id}`} className="text-[11px] text-neutral-400" title="You lack Edit permission for HO Approvals">🔒 view only</span>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-xs text-neutral-400">Nothing pending here.</td></tr>}
        </tbody>
      </table>
    </div>
  </div>
);

export default HOPortal;
